import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { getSearchIndex, festivalToSearchEntry, addArtistEntries, addAfterpartyEntries, type SearchEntry } from "@/lib/search-index";
import { useFestivalRegistry, useFestivalById } from "@/hooks/use-festival-registry";
import { useQuery } from "@tanstack/react-query";

const RECENT_SEARCHES_KEY = "troopn_recent_searches";
const MAX_RECENT = 5;
const DEBOUNCE_MS = 300;

export type SearchResultGroup = {
  type: SearchEntry["type"];
  label: string;
  items: SearchEntry[];
  total: number;
};

export function useGlobalSearch() {
  const { data: festivals } = useFestivalRegistry();
  const festivalEntries = useMemo(
    () => festivals?.map(festivalToSearchEntry) ?? [],
    [festivals]
  );

  // Load artist + afterparty data from all festival configs for global search
  const { data: allFestivalConfigs } = useQuery<Array<{ id: string; name: string; artists?: Array<{ id: string; name: string; genre: string; stageId: string }>; afterParties?: Array<{ id: string; name: string; venue: string; genre: string; headliners: string[] }>; stages?: Array<{ id: string; name: string }> }>>({
    queryKey: ["/api/festivals/configs-search"],
    enabled: (festivals?.length ?? 0) > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Populate search index with artists + afterparties from all festivals
  useMemo(() => {
    if (!allFestivalConfigs?.length) return;
    const artists: Parameters<typeof addArtistEntries>[0] = [];
    const afterparties: Parameters<typeof addAfterpartyEntries>[0] = [];
    for (const f of allFestivalConfigs) {
      const stageLookup = new Map((f.stages || []).map(s => [s.id, s.name]));
      for (const a of f.artists || []) {
        artists.push({ id: `${f.id}--${a.id}`, name: a.name, genre: a.genre, festivalId: f.id, festivalName: f.name, venue: stageLookup.get(a.stageId) });
      }
      for (const p of f.afterParties || []) {
        afterparties.push({ id: `${f.id}--${p.id}`, name: p.name, venue: p.venue, genre: p.genre, festivalId: f.id, headliners: p.headliners });
      }
    }
    if (artists.length) addArtistEntries(artists);
    if (afterparties.length) addAfterpartyEntries(afterparties);
  }, [allFestivalConfigs]);

  const fuseIndex = getSearchIndex(festivalEntries);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce the query
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query]);

  // All results from fuse
  const allResults = useMemo(() => {
    if (debouncedQuery.length < 2) return [];
    return fuseIndex.search(debouncedQuery).map((r) => r.item);
  }, [fuseIndex, debouncedQuery]);

  // Grouped results (top 3 per group)
  const groupedResults = useMemo((): SearchResultGroup[] => {
    const groups: Record<string, SearchEntry[]> = {};
    for (const item of allResults) {
      if (!groups[item.type]) groups[item.type] = [];
      groups[item.type].push(item);
    }

    const typeOrder: SearchEntry["type"][] = ["festival", "artist", "afterparty", "journey", "event"];
    const typeLabels: Record<string, string> = {
      festival: "Festivals",
      artist: "Artists & DJs",
      afterparty: "After-Parties",
      journey: "Journeys",
      event: "Events",
    };

    return typeOrder
      .filter((t) => groups[t]?.length)
      .map((t) => ({
        type: t,
        label: typeLabels[t] || t,
        items: groups[t].slice(0, 3),
        total: groups[t].length,
      }));
  }, [allResults]);

  // Filtered results (when a filter tab is active)
  const filteredResults = useMemo(() => {
    if (activeFilter === "all") return allResults;
    return allResults.filter((r) => r.type === activeFilter);
  }, [allResults, activeFilter]);

  const saveRecentSearch = useCallback((term: string) => {
    const trimmed = term.trim();
    if (!trimmed || trimmed.length < 2) return;
    setRecentSearches((prev) => {
      const updated = [trimmed, ...prev.filter((s) => s !== trimmed)].slice(0, MAX_RECENT);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch {
        // localStorage full or unavailable
      }
      return updated;
    });
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch {
      // ignore
    }
  }, []);

  const clearQuery = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    setActiveFilter("all");
  }, []);

  return {
    query,
    setQuery,
    debouncedQuery,
    allResults,
    groupedResults,
    filteredResults,
    activeFilter,
    setActiveFilter,
    recentSearches,
    saveRecentSearch,
    clearRecentSearches,
    clearQuery,
    isSearching: debouncedQuery.length >= 2,
  };
}
